import * as RadixDropdownMenu from "@radix-ui/react-dropdown-menu";
import {
	type FC,
	type ForwardRefRenderFunction,
	type ReactNode,
	type RefAttributes,
	forwardRef,
	useRef,
} from "react";

import { CheckIcon, ChevronRightIcon, IndeterminateCheckBoxIcon } from "@moment/icons";

import { BasicTooltip, TooltipContent, TooltipTrigger } from "../Tooltip";
import { cn } from "../utils";

const DropdownMenuRoot: FC<RadixDropdownMenu.DropdownMenuProps> = ({
	children,
	modal: modalRaw,
	...props
}) => {
	const modal = modalRaw === undefined ? false : modalRaw;
	return (
		<RadixDropdownMenu.Root modal={modal} {...props}>
			{children}
		</RadixDropdownMenu.Root>
	);
};

const DropdownMenuTrigger: ForwardRefRenderFunction<
	HTMLButtonElement,
	RadixDropdownMenu.DropdownMenuTriggerProps & RefAttributes<HTMLButtonElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixDropdownMenu.Trigger ref={ref} {...props}>
			{children}
		</RadixDropdownMenu.Trigger>
	);
};

const DropdownMenuSubTrigger: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuSubTriggerProps &
		RefAttributes<HTMLDivElement> & { disabledMessage?: ReactNode }
> = ({ children, ...props }, ref) => {
	const item = (
		<RadixDropdownMenu.SubTrigger
			{...props}
			ref={ref}
			className="DropdownMenuSubTrigger flex cursor-default flex-row items-center px-2 py-1.5 mx-1 rounded"
		>
			<div className="mr-2 flex flex-row items-center gap-2">{children}</div>
			<ChevronRightIcon className="ml-auto" />
		</RadixDropdownMenu.SubTrigger>
	);

	if (props.disabled && props.disabledMessage) {
		return (
			<BasicTooltip>
				<TooltipTrigger asChild>
					<button className="flex w-full cursor-default flex-col items-stretch">
						{item}
					</button>
				</TooltipTrigger>
				<TooltipContent side="right">
					<div className="max-w-[16rem]">{props.disabledMessage}</div>
				</TooltipContent>
			</BasicTooltip>
		);
	}

	return item;
};

const DropdownMenuContent: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuContentProps & RefAttributes<HTMLDivElement>
> = ({ children, onCloseAutoFocus, onPointerDownOutside, ...props }, ref) => {
	const pointerDownOutsideRef = useRef(false);

	return (
		<RadixDropdownMenu.Content
			{...props}
			ref={ref}
			onPointerDownOutside={(event) => {
				pointerDownOutsideRef.current = true;
				onPointerDownOutside?.(event);
			}}
			onCloseAutoFocus={(event) => {
				onCloseAutoFocus?.(event);

				if (pointerDownOutsideRef.current) {
					pointerDownOutsideRef.current = false;
					if (event.defaultPrevented) return;
					event.preventDefault();
				}
			}}
			className={cn(
				"data-[state=open]:animate-in",
				"data-[state=closed]:animate-out",
				"data-[state=closed]:fade-out-0",
				"data-[state=open]:fade-in-0",
				"data-[state=closed]:zoom-out-95",
				"data-[state=open]:zoom-in-95",
				"data-[side=bottom]:slide-in-from-top-2",
				"data-[side=left]:slide-in-from-right-2",
				"data-[side=right]:slide-in-from-left-2",
				"data-[side=top]:slide-in-from-bottom-2",
				"rounded-md",
				"shadow-xl",
				"z-popover",
				props.className
			)}
			sideOffset={4}
		>
			<div
				className={cn(
					"overflow-hidden",
					"rounded-md",
					"border",
					"border-secondary",
					"bg-primary-raised",
					"text-sm"
				)}
			>
				<div className="DropdownMenuContentScrollContainer max-h-[32rem] overflow-y-auto py-1">
					{children}
				</div>
			</div>
		</RadixDropdownMenu.Content>
	);
};

const DropdownMenuLabel: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuLabelProps & React.HTMLAttributes<HTMLDivElement>
> = ({ children, ...props }, ref) => {
	return (
		<RadixDropdownMenu.Label
			{...props}
			ref={ref}
			className={cn(
				"DropdownMenuItem",
				"font-bold",
				"text-tertiary",
				"text-xs",
				"tracking-wider",
				`flex cursor-pointer flex-row items-center gap-2 px-2 py-1.5 mx-1 rounded`
			)}
		>
			{children}
		</RadixDropdownMenu.Label>
	);
};

const DropdownMenuItem: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuItemProps &
		RefAttributes<HTMLDivElement> & {
			variant?: "default" | "negative";
			disabledMessage?: ReactNode;
		}
> = ({ children, variant, className, ...props }, ref) => {
	let variantClass: string;
	switch (variant) {
		case "negative":
			variantClass = "DropdownMenuItem DropdownMenuItem--negative";
			break;

		default:
			variantClass = "DropdownMenuItem DropdownMenuItem--default";
			break;
	}

	const item = (
		<RadixDropdownMenu.Item
			{...props}
			ref={ref}
			className={cn(
				variantClass,
				className,
				"group flex cursor-pointer flex-row items-center gap-2 px-2 py-1.5 mx-1 rounded"
			)}
		>
			{children}
		</RadixDropdownMenu.Item>
	);

	if (props.disabled && props.disabledMessage) {
		return (
			<BasicTooltip>
				<TooltipTrigger asChild>
					<button className="w-full cursor-default">{item}</button>
				</TooltipTrigger>
				<TooltipContent side="right">
					<div className="max-w-[16rem]">{props.disabledMessage}</div>
				</TooltipContent>
			</BasicTooltip>
		);
	}

	return item;
};

const DropdownMenuSubContent: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuSubContentProps & RefAttributes<HTMLDivElement>
> = ({ children, sideOffset = 4, ...props }, ref) => {
	return (
		<RadixDropdownMenu.SubContent
			{...props}
			ref={ref}
			className={cn(
				"data-[state=open]:animate-in",
				"data-[state=closed]:animate-out",
				"data-[state=closed]:fade-out-0",
				"data-[state=open]:fade-in-0",
				"data-[state=closed]:zoom-out-95",
				"data-[state=open]:zoom-in-95",
				"data-[side=bottom]:slide-in-from-top-2",
				"data-[side=left]:slide-in-from-right-2",
				"data-[side=right]:slide-in-from-left-2",
				"data-[side=top]:slide-in-from-bottom-2",
				props.className,
				"-mt-1",
				"max-w-xs",
				"overflow-hidden",
				"rounded-md",
				"border",
				"py-1",
				"text-sm",
				"shadow-lg",
				"z-popover",
				"bg-primary-raised"
			)}
			sideOffset={sideOffset}
		>
			{children}
		</RadixDropdownMenu.SubContent>
	);
};

const DropdownMenuSeparator: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuSeparatorProps & RefAttributes<HTMLDivElement>
> = (_, ref) => {
	return <RadixDropdownMenu.Separator ref={ref} className="my-1 border-t" />;
};

const DropdownMenuFooter: FC<{ children: ReactNode }> = ({ children }) => {
	return (
		<>
			{/* `-mb-2` is required here to show up at the bottom, countering the `py-2` in the parent */}
			<RadixDropdownMenu.Label className="-mb-1.5 mt-2 border-t bg-tertiary px-2 py-1.5 mx-1 rounded italic text-secondary">
				{children}
			</RadixDropdownMenu.Label>
		</>
	);
};

const DropdownCheckboxItem: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuCheckboxItemProps & RefAttributes<HTMLDivElement>
> = ({ className, checked, children, ...props }, ref) => {
	let checkMark = <CheckIcon className="opacity-0" />;
	if (checked === "indeterminate") {
		checkMark = <IndeterminateCheckBoxIcon className="text-inverse-primary" />;
	} else if (checked) {
		checkMark = (
			<CheckIcon className="absolute left-0 top-0 h-full w-full text-inverse-primary" />
		);
	}

	return (
		<RadixDropdownMenu.CheckboxItem
			{...props}
			ref={ref}
			checked={checked}
			className={cn(
				className,
				"DropdownMenuCheckboxItem group flex cursor-pointer flex-row items-center gap-2 px-2 py-1.5 mx-1 rounded"
			)}
		>
			<div className="CheckboxItemMark relative h-4 w-4">{checkMark}</div>
			{children}
		</RadixDropdownMenu.CheckboxItem>
	);
};

const DropdownRadioItem: ForwardRefRenderFunction<
	HTMLDivElement,
	RadixDropdownMenu.DropdownMenuRadioItemProps &
		RefAttributes<HTMLDivElement> & {
			level?: "primary" | "secondary";
			disabledMessage?: ReactNode;
		}
> = ({ className, children, ...props }, ref) => {
	const item = (
		<RadixDropdownMenu.RadioItem
			{...props}
			ref={ref}
			className={cn(
				className,
				props.level === "secondary" && "italic text-secondary",
				"DropdownMenuRadioItem flex cursor-pointer flex-row items-center gap-2 px-2 py-1.5 mx-1 rounded"
			)}
		>
			<div className="mr-2 h-4 w-4">
				<DropdownMenu.ItemIndicator className="flex items-center justify-center">
					<CheckIcon />
				</DropdownMenu.ItemIndicator>
			</div>
			{children}
		</RadixDropdownMenu.RadioItem>
	);

	if (props.disabled && props.disabledMessage) {
		return (
			<BasicTooltip>
				<TooltipTrigger asChild>
					<button className="w-full cursor-default">{item}</button>
				</TooltipTrigger>
				<TooltipContent side="right">
					<div className="max-w-[16rem]">{props.disabledMessage}</div>
				</TooltipContent>
			</BasicTooltip>
		);
	}

	return item;
};

/**
 * Styled variant of the Radix `DropdownMenu` component.
 */
export const DropdownMenu = {
	Root: DropdownMenuRoot,

	Trigger: forwardRef(DropdownMenuTrigger),

	Portal: RadixDropdownMenu.Portal,

	Content: forwardRef(DropdownMenuContent),

	Label: forwardRef(DropdownMenuLabel),
	Item: forwardRef(DropdownMenuItem),

	Group: RadixDropdownMenu.Group,

	CheckboxItem: forwardRef(DropdownCheckboxItem),

	RadioGroup: RadixDropdownMenu.RadioGroup,
	RadioItem: forwardRef(DropdownRadioItem),
	ItemIndicator: RadixDropdownMenu.ItemIndicator,

	Sub: RadixDropdownMenu.Sub,
	SubTrigger: forwardRef(DropdownMenuSubTrigger),
	SubContent: forwardRef(DropdownMenuSubContent),

	Separator: forwardRef(DropdownMenuSeparator),

	Arrow: RadixDropdownMenu.Arrow,

	Footer: DropdownMenuFooter,
};
